import React, { FC, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Button, DeleteModal, ExportTableDataModal, PaginationController, SpinnerIcon } from '@sistemiv/s-components'
import { useTranslation } from 'react-i18next'
import { PlusIcon } from '@heroicons/react/24/solid'
import Table from './NamedValuesTable'
import { TableRow } from '../table/Table'
import AddNamedValueModal from './AddNamedValueModal'
import { useAddNamedValue } from '../../repositories/named-values/mutation/add-named-value.mutation'
import { useNamedValues } from '../../repositories/named-values'
import { useDeleteNamedValue } from '../../repositories/named-values/mutation/delete-named-value.mutation'
import EditNamedValueModal from './EditNamedValueModal'
import exportTableData from '../../utils/exportTableData'
import NamedValuesService from '../../services/NamedValues.service'

const NamedValuesPage: FC = () => {
  const { org } = useParams()
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(20)
  const [addValueModalOpen, setAddValueModalOpen] = useState(false)
  const [tableExportLoading, setTableExportLoading] = useState(false)
  const [nameSearch, setNameSearch] = useState('')
  const [valueSearch, setValueSearch] = useState('')
  const { t } = useTranslation()
  const [tableFilters, setTableFilters] = useState<{ [key: string]: any }>({
    orgPosition: [],
    credentialType: [],
  })
  const { data: namedValues, isLoading, isPending } = useNamedValues(page, size, nameSearch, valueSearch)
  const { mutate: addValue, isPending: addingValue } = useAddNamedValue()
  const [editingValue, setEditingValue] = useState<any>(undefined)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedRows, setSelectedRows] = useState<TableRow[]>([])
  const [isExportOpen, setExportOpen] = useState(false)
  const { mutateAsync: deleteNamedValueMutation, isPending: removingValue } = useDeleteNamedValue()

  const newRows = namedValues?.items?.map((value) => {
    const retval: TableRow = {
      id: {
        type: 'plainText',
        value: value?.name,
      },
      name: {
        type: 'plainText',
        value: value?.name,
      },
      value: {
        type: 'plainText',
        value: value?.value,
      },
    }

    return retval
  })

  const headers = [
    {
      accessor: 'name',
      label: t('NamedValues.name'),
      searchType: 'search',
    },
    {
      accessor: 'value',
      label: t('NamedValues.value'),
      searchType: 'search',
    },
    {
      accessor: 'actions',
      label: t('NamedValues.actions'),
      searchType: '',
    },
  ]
  useEffect(() => {
    if (!namedValues) return
    if (namedValues?.total < page * size) {
      setPage(0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [namedValues, size, page])

  const onEditClick = (id: string) => {
    setEditingValue(namedValues?.items?.find((value) => value?.name === id))
    setEditModalOpen(true)
  }

  const onDeleteClick = (id: string) => {
    setEditingValue(namedValues?.items?.find((value) => value?.name === id))
    setDeleteModalOpen(true)
  }

  const handleCreateNamedValue = (name: string, value: string) => {
    addValue(
      {
        body: { name: name, value: value },
      },
      {
        onSuccess: () => {
          setAddValueModalOpen(false)
        },
      },
    )
  }

  const handleDeleteAcc = () => {
    if (!org || !editingValue) return
    deleteNamedValueMutation(
      { id: editingValue.name },
      {
        onSuccess: () => {
          setDeleteModalOpen(false)
          setEditingValue(undefined)
        },
      },
    )
  }

  const handleEditCancel = () => {
    setEditModalOpen(false)
    setEditingValue(undefined)
  }

  const handleExport = async (exportOptions: any) => {
    setTableExportLoading(true)
    if (!org) return
    let values = namedValues
    if (namedValues?.total > size) {
      values = await NamedValuesService.listNamedValues(org, 1, namedValues?.total)
    }
    let exportingInstances = values?.items
    if (exportOptions.mode === 'Export selected') {
      exportingInstances = values.items.filter((el) => selectedRows.find((row) => row['id'].value === el.name))
    }
    if (exportingInstances.length === 0) return

    exportTableData({
      xlsFormat: exportOptions.exportExcel,
      csvFormat: exportOptions.exportCsv,
      tablePayload: { EnvVariables: exportingInstances },
      setTableExportLoading,
    })
  }

  if (tableExportLoading)
    return (
      <div className='w-full flex justify-center pt-5'>
        <div className='flex flex-col gap-y-2 items-center justify-center'>
          <SpinnerIcon className='text-sky-500 h-7 w-7' />
          {t('NamedValues.exportingTable')}
        </div>
      </div>
    )

  return (
    <div className='overflow-hidden flex flex-col h-full w-full'>
      <div className='flex flex-none items-center justify-between gap-x-16 border-b border-gray-300 px-8 py-3'>
        <div className='flex gap-x-16'>
          <p className='text-2xl'>{t('namedValues')}</p>
          <div className='flex items-center gap-x-4'>
            <Button
              onClick={() => setAddValueModalOpen(true)}
              className='text-blue-500 border border-blue-500 hover:bg-slate-50 active:!bg-slate-100 flex items-center gap-x-2'
            >
              <PlusIcon className='w-4 h-4 flex-none' />
              <>{t('NamedValues.namedValue')}</>
            </Button>
          </div>
          {namedValues?.total > 0 && (
            <div data-testid='export-button'>
              <Button className='border border-blue-500 text-blue-500' onClick={() => setExportOpen(true)}>
                {t('NamedValues.export')}
              </Button>
            </div>
          )}
        </div>
        <PaginationController
          total={namedValues?.total ?? 1}
          size={size}
          page={page + 1}
          onPageChange={(page) => setPage(page - 1)}
          onSizeChange={(size) => setSize(size)}
        />
      </div>
      <div className={`px-8 pt-3 py-1 grow overflow-hidden ${isLoading ? 'opacity-50' : 'opacity-100'}`}>
        <div className='w-full h-full overflow-auto relative pb-32'>
          {(isLoading || isPending) && (
            <div className='absolute first:w-full flex justify-center items-center pt-5'>
              <SpinnerIcon className='text-sky-500 h-7 w-7'></SpinnerIcon>
            </div>
          )}
          <Table
            headers={headers}
            rows={newRows}
            selectedRows={selectedRows}
            setSelectedRows={setSelectedRows}
            onEdit={onEditClick}
            onDelete={onDeleteClick}
            onNameSearch={(value) => {
              setNameSearch(value)
            }}
            onValueSearch={setValueSearch}
            onHeaderFilter={(field, value) =>
              setTableFilters((old) => {
                return {
                  ...old,
                  [field]: value ?? undefined,
                }
              })
            }
            filterValues={tableFilters}
          />
        </div>
      </div>
      {addValueModalOpen && (
        <AddNamedValueModal
          open={addValueModalOpen}
          setOpen={setAddValueModalOpen}
          isAdding={addingValue}
          onAdd={handleCreateNamedValue}
        />
      )}
      {deleteModalOpen && (
        <DeleteModal
          title={t('NamedValues.delete')}
          description={t('NamedValues.deleteMsg')}
          open={deleteModalOpen}
          setOpen={setDeleteModalOpen}
          onDeleteConfirmed={handleDeleteAcc}
          isLoading={removingValue}
        />
      )}
      {editModalOpen && (
        <EditNamedValueModal
          open={editModalOpen}
          setOpen={setEditModalOpen}
          onCancel={handleEditCancel}
          editingValue={editingValue}
        />
      )}
      {isExportOpen && (
        <ExportTableDataModal
          open={isExportOpen}
          setOpen={setExportOpen}
          onConfirm={(val) => {
            handleExport(val)
            setExportOpen(false)
          }}
          selectableRows={true}
        />
      )}
    </div>
  )
}

export default NamedValuesPage
